import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export default function KineticHero({ eyebrow, lines, sub, image, children, tall = false, badge = false, bright = false }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="kinetic-hero"
            className={`relative overflow-hidden flex items-end ${tall ? "min-h-[100svh]" : "min-h-[72vh]"}`}
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <img src={image} alt="Clear trout stream riverbank during an early season hatch — Trout Start fly fishing guides" className={`w-full h-[125%] object-cover ${bright ? "opacity-60" : "opacity-35"}`} />
                <div className={`absolute inset-0 bg-gradient-to-b ${bright ? "from-[#0B1320]/55 via-[#0B1320]/15" : "from-[#0B1320]/80 via-[#0B1320]/30"} to-[#0B1320]`} />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B1320]/70 to-transparent" />
            </motion.div>

            {badge && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ duration: 1.1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute right-6 sm:right-16 lg:right-24 top-32 sm:top-36 hidden sm:block"
                >
                    <div className="relative animate-float-slow">
                        <div className="absolute inset-0 rounded-full bg-teal-glow blur-3xl scale-125" />
                        <div className="absolute -inset-3 rounded-full border border-teal/25 animate-ripple" />
                        <img
                            src="/logo-teal.jpg"
                            alt="Trout Start badge"
                            className="relative w-44 lg:w-60 rounded-full shadow-2xl shadow-black/60 ring-1 ring-white/15"
                        />
                    </div>
                </motion.div>
            )}

            <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pb-20 sm:pb-28 pt-44">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="font-mono text-xs sm:text-sm tracking-[0.32em] uppercase text-teal mb-6"
                >
                    {eyebrow}
                </motion.p>

                <h1 className="font-serif font-bold tracking-tight leading-[1.02] text-slate-50">
                    {lines.map((line, i) => (
                        <span key={i} className="mask-line">
                            <motion.span
                                initial={{ y: "112%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                                className={`block text-4xl sm:text-5xl lg:text-6xl ${line.accent === "coral" ? "text-coral" : line.accent === "teal" ? "text-teal" : ""}`}
                            >
                                {line.text}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.25 + lines.length * 0.14 + 0.1 }}
                    className="mt-7 text-lg sm:text-xl font-sans text-slate-400 max-w-2xl"
                >
                    {sub}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.25 + lines.length * 0.14 + 0.28 }}
                    className="mt-10 flex flex-wrap gap-4"
                >
                    {children}
                </motion.div>
            </motion.div>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 opacity-60">
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400">Scroll</span>
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                    className="block w-px h-8 bg-gradient-to-b from-teal to-transparent"
                />
            </div>
        </section>
    );
}
